import { useContext, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { ClientProps, WellProps } from "../../interfaces/interfaces";
import { Spinner } from "../ui/Spinner";
import AuthContext from "../../context/AuthContext";
import { DocumentsTopNavbar } from "./DocumentsTopNavbar";

export type DocRow = {
  rowId: string;
  customName: string;
  date: string;
  wellId?: number;
  wellName: string;
  source: "uploaded" | "app";
  path?: string;
  mimeType?: string;
  originalFileName?: string;
  appId?: number;
  owner: string;
  proposalId?: number | null;
  tallyId?: number | null;
  pullingDesignId?: number | null;
  installationDate?: string;
  defaultUploadPath?: string;
  defaultUploadName?: string;
};

type DateRange = "all" | "7" | "30" | "90" | "ytd" | "custom";
type Source = "all" | "uploaded" | "app";

interface Props {
  title: string;
  subtitle: string;
  wells: WellProps[];
  wellsLoading: boolean;
  rows: DocRow[];
  rowsLoading: boolean;
  selectedWellId: number | null;
  onWellSelect: (id: number | null) => void;
  onDownload: (row: DocRow) => Promise<void>;
  showSource?: boolean;
  noWellMessage?: string;
  isError?: boolean;
  hideWellsPanel?: boolean;
  showClientFilter?: boolean;
  selectedClientId?: number | null;
  // Admin-only props
  isAdmin?: boolean;
  clientOptions?: ClientProps[];
  onClientChange?: (client: ClientProps | null) => void;
  onUpload?: () => void;
  onOpenInNew?: (row: DocRow) => void;
  onDelete?: (row: DocRow) => void;
  onEditUploaded?: (row: DocRow) => void;
  onEdit?: (row: DocRow) => void;
}

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

const applyDateFilter = (
  rows: DocRow[],
  range: DateRange,
  from: string,
  to: string,
): DocRow[] => {
  if (range === "all") return rows;
  const today = new Date();
  let min: Date | null = null;
  let max: Date | null = null;
  if (range === "7") { min = new Date(today); min.setDate(min.getDate() - 7); }
  else if (range === "30") { min = new Date(today); min.setDate(min.getDate() - 30); }
  else if (range === "90") { min = new Date(today); min.setDate(min.getDate() - 90); }
  else if (range === "ytd") { min = new Date(today.getFullYear(), 0, 1); }
  else if (range === "custom") {
    if (from) min = new Date(from + "T00:00:00");
    if (to) max = new Date(to + "T23:59:59");
  }
  return rows.filter((r) => {
    const d = new Date(r.date + "T12:00:00");
    if (min && d < min) return false;
    if (max && d > max) return false;
    return true;
  });
};

// Design tokens
const OG = "#ED8B00";
const CARD = "#fff";
const PAGE_BG = "#F3F3F0";
const BORDER = "#E7E7E3";
const BORDER_LIGHT = "#EFEFEC";
const BORDER_GRID = "#F2F2EF";
const TEXT = "#2A2F38";
const TEXT_MID = "#5A6170";
const TEXT_MUTED = "#8A8F99";
const TEXT_FAINT = "#9CA1AB";
const SEARCH_BG = "#FAFAF8";
const DARK_BG = "#363D4A";

const segBase: React.CSSProperties = {
  border: "none", borderRadius: 7, padding: "7px 13px", fontSize: 12.5,
  fontWeight: 600, cursor: "pointer", background: "transparent",
  color: "#7A808C", fontFamily: "inherit",
};
const segOn: React.CSSProperties = {
  ...segBase, background: CARD, color: OG, boxShadow: "0 1px 2px rgba(0,0,0,.08)",
};
const navBtn: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 8, border: `1px solid #E2E2DD`,
  background: CARD, display: "flex", alignItems: "center",
  justifyContent: "center", cursor: "pointer", color: "#4A5160",
};
const navBtnDisabled: React.CSSProperties = {
  ...navBtn, color: "#CFD2D8", cursor: "not-allowed", opacity: 0.5,
};

export const DocumentsPortalLayout = ({
  title,
  subtitle,
  wells,
  wellsLoading,
  rows,
  rowsLoading,
  selectedWellId,
  onWellSelect,
  onDownload,
  showSource = true,
  noWellMessage,
  isError = false,
  hideWellsPanel = false,
  showClientFilter = false,
  selectedClientId = null,
  isAdmin = false,
  clientOptions,
  onClientChange,
  onUpload,
  onOpenInNew,
  onDelete,
  onEditUploaded,
  onEdit,
}: Props) => {
  const { isDrawerOpen } = useContext(AuthContext);
  const drawerWidth = isDrawerOpen ? 250 : 64;

  const [wellSearch, setWellSearch] = useState("");
  const [docSearch, setDocSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [source, setSource] = useState<Source>("all");
  const [owner, setOwner] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  };

  const handleDownload = async (row: DocRow) => {
    setIsBusy(true);
    showToast(`Downloading "${row.customName}"…`);
    try { await onDownload(row); } finally { setIsBusy(false); }
  };

  const handleBulkDownload = async () => {
    const items = filtered.filter((r) => selectedIds.includes(r.rowId));
    showToast(`Downloading ${items.length} document${items.length > 1 ? "s" : ""}…`);
    setIsBusy(true);
    try {
      for (const item of items) {
        await onDownload(item);
        if (items.length > 1) await new Promise((r) => setTimeout(r, 350));
      }
    } finally { setIsBusy(false); }
  };

  // ---- Filter wells ----
  const filteredWells = wellSearch.trim()
    ? wells.filter((w) => w.name.toLowerCase().includes(wellSearch.toLowerCase()))
    : wells;

  const selectedWell = wells.find((w) => w.id === selectedWellId);

  // ---- Filter documents ----
  let filtered = selectedWellId != null && !hideWellsPanel
    ? rows.filter((r) => r.wellId === selectedWellId)
    : rows;
  if (docSearch.trim())
    filtered = filtered.filter((r) =>
      r.customName.toLowerCase().includes(docSearch.toLowerCase()),
    );
  if (showSource && source !== "all")
    filtered = filtered.filter((r) => r.source === source);
  if (owner !== "all") filtered = filtered.filter((r) => r.owner === owner);
  filtered = applyDateFilter(filtered, dateRange, dateFrom, dateTo);

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );
  const hasActiveFilters =
    docSearch.trim() !== "" ||
    (showSource && source !== "all") ||
    owner !== "all" ||
    dateRange !== "all";

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const curPage = Math.min(page, totalPages - 1);
  const pageStart = total === 0 ? 0 : curPage * rowsPerPage + 1;
  const pageEnd = Math.min((curPage + 1) * rowsPerPage, total);
  const pageItems = filtered.slice(curPage * rowsPerPage, pageEnd);

  const allOnPageChecked =
    pageItems.length > 0 && pageItems.every((r) => selectedIds.includes(r.rowId));

  const toggleAll = () => {
    if (allOnPageChecked) {
      setSelectedIds((p) => p.filter((id) => !pageItems.some((r) => r.rowId === id)));
    } else {
      setSelectedIds((p) => [
        ...p, ...pageItems.map((r) => r.rowId).filter((id) => !p.includes(id)),
      ]);
    }
  };

  const clearFilters = () => {
    setDocSearch(""); setSource("all"); setOwner("all");
    setDateRange("all"); setDateFrom(""); setDateTo(""); setPage(0);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: "9px 12px 9px 34px",
    border: "1px solid #E2E2DD", borderRadius: 9, fontSize: 13,
    outline: "none", background: SEARCH_BG, color: TEXT,
    fontFamily: "inherit", boxSizing: "border-box",
  };
  const selectStyle: React.CSSProperties = {
    padding: "8px 11px", border: "1px solid #E2E2DD", borderRadius: 9,
    fontSize: 13, background: SEARCH_BG, color: TEXT, outline: "none",
    cursor: "pointer", fontFamily: "inherit",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 10.5, fontWeight: 600, letterSpacing: ".6px",
    textTransform: "uppercase", color: TEXT_FAINT,
    display: "block", marginBottom: 5,
  };

  const actionColWidth = isAdmin ? (onEditUploaded ? "150px" : "116px") : "72px";
  const gridCols = hideWellsPanel
    ? showSource
      ? `38px minmax(180px,2fr) 110px 122px 1.2fr ${actionColWidth}`
      : `38px minmax(220px,2fr) 120px 1.2fr ${actionColWidth}`
    : showSource
    ? `38px minmax(150px,1.8fr) 1.1fr 100px 122px 1.2fr ${actionColWidth}`
    : `38px minmax(180px,2fr) 1.2fr 120px 1.2fr ${actionColWidth}`;
  const shouldShowClientFilter = Boolean(
    clientOptions?.length && onClientChange && (isAdmin || showClientFilter),
  );
  const clientSelect = shouldShowClientFilter ? (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 210 }}>
      <label style={labelStyle}>Client</label>
      <select
        onChange={(e) => {
          const c =
            clientOptions?.find((cl) => String(cl.id) === e.target.value) ??
            null;
          setPage(0);
          setSelectedIds([]);
          onClientChange?.(c);
        }}
        value={selectedClientId ? String(selectedClientId) : ""}
        style={{ ...selectStyle, width: "100%" }}
      >
        <option value="" disabled>
          Select a client...
        </option>
        {clientOptions?.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.name}
          </option>
        ))}
      </select>
    </Box>
  ) : null;

  if (!hideWellsPanel && wellsLoading && wells.length === 0) return <Spinner />;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        background: PAGE_BG,
        fontFamily: "'Public Sans', Helvetica, Arial, sans-serif",
        fontSize: 14,
        color: TEXT,
        transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 1,
      }}
    >
      <DocumentsTopNavbar
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {isAdmin && onUpload && (
              <Box
                component="button"
                onClick={onUpload}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  background: "transparent", color: TEXT,
                  border: `1px solid ${BORDER}`, borderRadius: "9px",
                  px: "15px", py: "9px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit",
                  "&:hover": { borderColor: OG, color: OG },
                }}
              >
                <UploadIcon />
                Upload
              </Box>
            )}
            {selectedIds.length > 0 && (
              <Box
                component="button"
                onClick={handleBulkDownload}
                disabled={isBusy}
                sx={{
                  display: "inline-flex", alignItems: "center",
                  gap: "8px", background: OG, color: "#fff", border: "none",
                  borderRadius: "9px", px: "15px", py: "9px",
                  fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                  "&:hover": { background: "#D97A00" },
                  "&:disabled": { opacity: 0.6, cursor: "wait" },
                }}
              >
                <DownloadIcon />
                Download {selectedIds.length} PDF{selectedIds.length > 1 ? "s" : ""}
              </Box>
            )}
          </>
        }
      />

      {/* Workspace */}
      <Box
        component="main"
        sx={{
          flex: 1, display: "grid",
          gridTemplateColumns: hideWellsPanel ? "1fr" : "316px 1fr",
          gap: "22px", p: "22px 26px",
          minHeight: 0, boxSizing: "border-box",
        }}
      >
        {!hideWellsPanel && (
          <Box sx={{
            display: "flex", flexDirection: "column", minHeight: 0,
            background: CARD, border: `1px solid ${BORDER}`,
            borderRadius: "14px", overflow: "hidden",
          }}>
          {shouldShowClientFilter && (
            <Box sx={{ p: "12px 18px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              {clientSelect}
            </Box>
          )}
          <Box sx={{ p: "16px 18px 12px", borderBottom: `1px solid ${BORDER_LIGHT}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "12px" }}>
              <Box sx={{ fontSize: 13, fontWeight: 700, letterSpacing: ".6px", textTransform: "uppercase", color: "#4A5160" }}>
                Wells
              </Box>
              <Box sx={{ background: "#F1F1ED", color: "#7A808C", fontSize: 11, fontWeight: 600, px: "9px", py: "2px", borderRadius: "20px" }}>
                {wells.length}
              </Box>
            </Box>
            <Box sx={{ position: "relative" }}>
              <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A6ABB5", display: "flex", pointerEvents: "none" }}>
                <SearchIcon />
              </Box>
              <input
                value={wellSearch}
                onChange={(e) => setWellSearch(e.target.value)}
                placeholder="Search wells…"
                style={inputStyle}
              />
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            {filteredWells.map((w) => {
              const sel = w.id === selectedWellId;
              return (
                <Box
                  key={w.id}
                  onClick={() => { onWellSelect(sel ? null : (w.id ?? null)); setPage(0); setSelectedIds([]); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: "11px",
                    py: "9px", px: "16px", cursor: "pointer",
                    borderBottom: `1px solid ${BORDER_GRID}`,
                    background: sel ? "#FDF3E3" : CARD,
                    "&:hover": { background: sel ? "#FDF3E3" : "#F7F7F4" },
                    fontSize: 13.5,
                  }}
                >
                  <Box sx={{ width: 3, height: 18, borderRadius: "3px", flexShrink: 0, background: sel ? OG : "transparent" }} />
                  <Box sx={{ color: sel ? "#A8650A" : "#3A4150", fontWeight: sel ? 700 : 500, letterSpacing: ".2px", flex: 1 }}>
                    {w.name}
                  </Box>
                  {sel && <Box sx={{ color: OG, display: "flex" }}><CheckIcon /></Box>}
                </Box>
              );
            })}
            {filteredWells.length === 0 && (
              <Box sx={{ p: "28px 18px", textAlign: "center", color: TEXT_FAINT, fontSize: 13 }}>
                No wells match "{wellSearch}".
              </Box>
            )}
          </Box>

          <Box sx={{ p: "11px 18px", borderTop: `1px solid ${BORDER_LIGHT}`, fontSize: 12, color: TEXT_MUTED }}>
            Showing {filteredWells.length} of {wells.length} wells
          </Box>
          </Box>
        )}

        {/* DOCUMENTS PANEL */}
        <Box sx={{
          display: "flex", flexDirection: "column", minHeight: 0,
          background: CARD, border: `1px solid ${BORDER}`,
          borderRadius: "14px", overflow: "hidden",
        }}>
          {/* Toolbar */}
          <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${BORDER_LIGHT}`, display: "flex", flexDirection: "column", gap: "13px" }}>
            {/* Count + well chip */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Box sx={{ fontSize: 15, fontWeight: 700, color: TEXT }}>
                {total} {title}
              </Box>
              {selectedWell && (
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  background: "#FDF1DE", color: "#B5710A", border: "1px solid #F2D7A8",
                  fontSize: 12, fontWeight: 600, py: "4px", pl: "11px", pr: "7px", borderRadius: "20px",
                }}>
                  Well: {selectedWell.name}
                  <Box component="span" onClick={() => { onWellSelect(null); setPage(0); setSelectedIds([]); }}
                    sx={{ cursor: "pointer", display: "flex", color: "#C5893A", "&:hover": { color: "#8a5500" } }}>
                    <CloseIcon />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Filters */}
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "11px", flexWrap: "wrap" }}>
              {hideWellsPanel && clientSelect}
              <Box sx={{ flex: 1, minWidth: 200, position: "relative" }}>
                <Box sx={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#A6ABB5", display: "flex", pointerEvents: "none" }}>
                  <SearchIcon />
                </Box>
                <input
                  value={docSearch}
                  onChange={(e) => { setDocSearch(e.target.value); setPage(0); }}
                  placeholder={`Search ${title.toLowerCase()}…`}
                  style={inputStyle}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <label style={labelStyle}>Date range</label>
                <select value={dateRange} onChange={(e) => { setDateRange(e.target.value as DateRange); setPage(0); }} style={selectStyle}>
                  <option value="all">All time</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="ytd">This year</option>
                  <option value="custom">Custom range</option>
                </select>
              </Box>

              {ownerOptions.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <label style={labelStyle}>Created by</label>
                  <select value={owner} onChange={(e) => { setOwner(e.target.value); setPage(0); }} style={selectStyle}>
                    <option value="all">All creators</option>
                    {ownerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Box>
              )}

              {showSource && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <label style={labelStyle}>Source</label>
                  <Box sx={{ display: "flex", background: "#F1F1ED", borderRadius: "9px", p: "3px", gap: "2px" }}>
                    <button onClick={() => { setSource("all"); setPage(0); }} style={source === "all" ? segOn : segBase}>All</button>
                    <button onClick={() => { setSource("app"); setPage(0); }} style={source === "app" ? segOn : segBase}>App</button>
                    <button onClick={() => { setSource("uploaded"); setPage(0); }} style={source === "uploaded" ? segOn : segBase}>Uploaded</button>
                  </Box>
                </Box>
              )}

              {hasActiveFilters && (
                <Box component="button" onClick={clearFilters}
                  sx={{ p: "8px 13px", border: "1px solid #E2E2DD", background: CARD, borderRadius: "9px", fontSize: 12.5, fontWeight: 600, color: "#6B7280", cursor: "pointer", fontFamily: "inherit", "&:hover": { borderColor: OG, color: OG } }}>
                  Clear filters
                </Box>
              )}
            </Box>

            {dateRange === "custom" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: 12, color: TEXT_MUTED }}>From</span>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  style={{ padding: "7px 10px", border: "1px solid #E2E2DD", borderRadius: 8, fontSize: 13, background: SEARCH_BG, outline: "none", fontFamily: "inherit" }} />
                <span style={{ fontSize: 12, color: TEXT_MUTED }}>to</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  style={{ padding: "7px 10px", border: "1px solid #E2E2DD", borderRadius: 8, fontSize: 13, background: SEARCH_BG, outline: "none", fontFamily: "inherit" }} />
              </Box>
            )}
          </Box>

          {/* Table header */}
          <Box sx={{
            display: "grid", gridTemplateColumns: gridCols,
            alignItems: "center", p: "11px 20px", background: "#FAFAF8",
            borderBottom: `1px solid ${BORDER_LIGHT}`, fontSize: 10.5,
            fontWeight: 700, letterSpacing: ".7px", textTransform: "uppercase",
            color: TEXT_FAINT, flexShrink: 0,
          }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <input type="checkbox" checked={allOnPageChecked} onChange={toggleAll}
                style={{ width: 15, height: 15, accentColor: OG, cursor: "pointer" }} />
            </Box>
            <Box>{title.replace(/s$/, "")}</Box>
            {!hideWellsPanel && <Box>Well</Box>}
            <Box>Date</Box>
            {showSource && <Box>Source</Box>}
            <Box>Created by</Box>
            <Box sx={{ textAlign: "right" }}>{isAdmin ? "ACTIONS" : "PDF"}</Box>
          </Box>

          {/* Rows */}
          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            {rowsLoading && rows.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                <Spinner />
              </Box>
            ) : pageItems.map((r) => {
              const checked = selectedIds.includes(r.rowId);
              const isApp = r.source === "app";
              return (
                <Box key={r.rowId} sx={{
                  display: "grid", gridTemplateColumns: gridCols,
                  alignItems: "center", p: "9px 20px",
                  borderBottom: `1px solid ${BORDER_GRID}`, fontSize: 13.5,
                  background: checked ? "#FDF6EC" : CARD,
                  "&:hover": { background: checked ? "#FDF6EC" : "#FBFAF7" },
                }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <input type="checkbox" checked={checked}
                      onChange={() => setSelectedIds((p) => p.includes(r.rowId) ? p.filter((id) => id !== r.rowId) : [...p, r.rowId])}
                      style={{ width: 15, height: 15, accentColor: OG, cursor: "pointer" }} />
                  </Box>
                  <Box sx={{ fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "10px" }} title={r.customName}>
                    {r.customName}
                  </Box>
                  {!hideWellsPanel && (
                    <Box sx={{ color: TEXT_MID, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "8px" }}>
                      {r.wellName || "—"}
                    </Box>
                  )}
                  <Box sx={{ color: TEXT_MID }}>{fmtDate(r.date)}</Box>
                  {showSource && (
                    <Box>
                      <Box component="span" sx={{
                        display: "inline-flex", alignItems: "center",
                        fontSize: 11.5, fontWeight: 600, py: "3px", px: "11px", borderRadius: "20px",
                        background: isApp ? "#FDF1DE" : "#EEF0F3",
                        color: isApp ? "#B5710A" : "#5A6372",
                        border: `1px solid ${isApp ? "#F2D7A8" : "#E0E3E8"}`,
                      }}>
                        {isApp ? "App Design" : "Uploaded"}
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ color: TEXT_MID, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pr: "8px" }}>
                    {r.owner}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "4px" }}>
                    {isAdmin ? (
                      r.source === "uploaded" ? (
                        <>
                          <Tooltip title="Open in new tab" arrow>
                            <Box component="button" onClick={() => onOpenInNew?.(r)}
                              sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" } }}>
                              <ExternalLinkIcon />
                            </Box>
                          </Tooltip>
                          <Tooltip title="Download" arrow>
                            <span style={{ display: "inline-flex" }}>
                              <Box component="button" onClick={() => !isBusy && handleDownload(r)} disabled={isBusy}
                                sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: isBusy ? "wait" : "pointer", opacity: isBusy ? 0.5 : 1, "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" } }}>
                                <DownloadIcon size={15} />
                              </Box>
                            </span>
                          </Tooltip>
                          {onDelete && (
                            <Tooltip title="Delete" arrow>
                              <Box component="button" onClick={() => onDelete(r)}
                                sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { borderColor: "#DC2626", color: "#DC2626", background: "#FEF2F2" } }}>
                                <TrashIcon />
                              </Box>
                            </Tooltip>
                          )}
                          {onEditUploaded && (
                            <Tooltip title="Edit" arrow>
                              <Box component="button" onClick={() => onEditUploaded(r)}
                                sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" } }}>
                                <EditPencilIcon />
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <>
                          <Tooltip title="Download PDF" arrow>
                            <span style={{ display: "inline-flex" }}>
                              <Box component="button" onClick={() => !isBusy && handleDownload(r)} disabled={isBusy}
                                sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: isBusy ? "wait" : "pointer", opacity: isBusy ? 0.5 : 1, "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" } }}>
                                <DownloadIcon size={15} />
                              </Box>
                            </span>
                          </Tooltip>
                          {onEdit && (
                            <Tooltip title="Edit Design" arrow>
                              <Box component="button" onClick={() => onEdit(r)}
                                sx={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E6E6E1", background: CARD, color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" } }}>
                                <EditPencilIcon />
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      )
                    ) : (
                      <Tooltip title="Download PDF" arrow>
                        <span style={{ display: "inline-flex" }}>
                          <Box component="button" onClick={() => !isBusy && handleDownload(r)} disabled={isBusy}
                            sx={{
                              width: 32, height: 32, borderRadius: 8,
                              border: "1px solid #E6E6E1", background: CARD,
                              color: "#6B7280", display: "flex", alignItems: "center",
                              justifyContent: "center", cursor: isBusy ? "wait" : "pointer",
                              opacity: isBusy ? 0.5 : 1,
                              "&:hover": { borderColor: OG, color: OG, background: "#FDF6EC" },
                            }}>
                            <DownloadIcon size={16} />
                          </Box>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}

            {total === 0 && !rowsLoading && (
              <Box sx={{ p: "56px 20px", textAlign: "center", color: TEXT_FAINT }}>
                <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: "#F4F4F0", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: "14px", color: "#C2C6CE" }}>
                  <DocIcon />
                </Box>
                <Box sx={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
                  {noWellMessage && rows.length === 0 && selectedWellId === null
                    ? noWellMessage
                    : `No ${title.toLowerCase()} match your filters`}
                </Box>
                {(!noWellMessage || rows.length > 0 || selectedWellId !== null) && (
                  <Box sx={{ fontSize: 13, mt: "4px" }}>
                    Try clearing a filter{hideWellsPanel ? "." : " or selecting a different well."}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Pagination */}
          <Box sx={{ p: "11px 20px", borderTop: `1px solid ${BORDER_LIGHT}`, display: "flex", alignItems: "center", gap: "16px", fontSize: 12.5, color: "#7A808C", flexShrink: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Rows per page</span>
              <select value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                style={{ padding: "5px 8px", border: "1px solid #E2E2DD", borderRadius: 7, fontSize: 12.5, background: SEARCH_BG, outline: "none", cursor: "pointer", fontFamily: "inherit" }}>
                <option value={8}>8</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </Box>
            <Box sx={{ ml: "auto" }}>{pageStart}–{pageEnd} of {total}</Box>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Box component="button" onClick={() => curPage > 0 && setPage(curPage - 1)}
                disabled={curPage === 0} style={curPage > 0 ? navBtn : navBtnDisabled}>
                <ChevronLeftIcon />
              </Box>
              <Box component="button" onClick={() => curPage < totalPages - 1 && setPage(curPage + 1)}
                disabled={curPage >= totalPages - 1} style={curPage < totalPages - 1 ? navBtn : navBtnDisabled}>
                <ChevronRightIcon />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Toast */}
      {toast && (
        <Box sx={{
          position: "fixed", bottom: 26, left: "50%", transform: "translateX(-50%)",
          background: DARK_BG, color: "#fff", p: "13px 20px", borderRadius: "11px",
          fontSize: 13.5, fontWeight: 500, boxShadow: "0 12px 32px rgba(20,24,31,.28)",
          display: "flex", alignItems: "center", gap: "11px", zIndex: 1400,
        }}>
          <Box sx={{ display: "flex", color: "#F5A623" }}><DownloadIcon /></Box>
          {toast}
        </Box>
      )}

      {isError && (
        <Box sx={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          background: "#DC2626", color: "#fff", p: "10px 18px",
          borderRadius: "9px", fontSize: 13, zIndex: 1400,
        }}>
          Something went wrong. Please try again.
        </Box>
      )}
    </Box>
  );
};

// ---- SVG icons ----

const SearchIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx={11} cy={11} r={7} /><line x1={21} y1={21} x2={16.65} y2={16.65} />
  </svg>
);
const CheckIcon = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CloseIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
    <line x1={18} y1={6} x2={6} y2={18} /><line x1={6} y1={6} x2={18} y2={18} />
  </svg>
);
const DownloadIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1={12} y1={15} x2={12} y2={3} />
  </svg>
);
const DocIcon = () => (
  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v5h5" /><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const ExternalLinkIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/><line x1={10} y1={14} x2={21} y2={3}/>
  </svg>
);
const TrashIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const EditPencilIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const UploadIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/><line x1={12} y1={3} x2={12} y2={15}/>
  </svg>
);
